package com.dualremotetest

import kotlin.js.Date
import kotlin.math.abs

/** performance.now() is monotonic and sub-millisecond; Date.now() is the fallback where it is missing. */
private val perf: dynamic = js("(typeof performance !== 'undefined') ? performance : null")

actual fun nanoTime(): Long =
    if (perf != null) ((perf.now() as Double) * 1_000_000.0).toLong() else (Date.now() * 1_000_000.0).toLong()

actual fun currentTimeMillis(): Long = Date.now().toLong()

private val SPEC = Regex("""%([-0]*)(\d+)?(?:\.(\d+))?([sdxXfc%])""")

actual fun String.format(vararg args: Any?): String {
    var next = 0
    return SPEC.replace(this) { m ->
        val (flags, widthText, precisionText, conv) = m.destructured
        if (conv == "%") return@replace "%"
        val arg = args.getOrNull(next++)
        val precision = precisionText.toIntOrNull()
        val body = when (conv) {
            "d" -> (arg as? Number)?.toLong()?.toString() ?: arg.toString()
            "x", "X" -> hex((arg as Number).toLong(), arg is Long).let { if (conv == "X") it.uppercase() else it }
            "f" -> fixed((arg as Number).toDouble(), precision ?: 6)
            "c" -> (arg as? Char)?.toString() ?: arg.toString()
            else -> arg.toString().let { if (precision != null) it.take(precision) else it }
        }
        val width = widthText.toIntOrNull() ?: 0
        when {
            body.length >= width -> body
            '-' in flags -> body.padEnd(width)
            '0' in flags && conv != "s" -> {
                val sign = if (body.startsWith("-")) "-" else ""
                sign + body.removePrefix(sign).padStart(width - sign.length, '0')
            }
            else -> body.padStart(width)
        }
    }
}

/** Two's-complement hex as Java prints it: 32 bits for an Int, 64 for a Long. */
private fun hex(v: Long, isLong: Boolean): String =
    if (isLong) v.toULong().toString(16) else v.toInt().toUInt().toString(16)

/** Fixed-point with half-up rounding, like Java's %.Nf. */
private fun fixed(v: Double, digits: Int): String {
    if (v.isNaN()) return "NaN"
    if (v.isInfinite()) return if (v > 0) "Infinity" else "-Infinity"
    val text = abs(v).asDynamic().toFixed(digits) as String
    return if (v < 0 && text.any { it in '1'..'9' }) "-$text" else text
}

private fun two(n: Int) = n.toString().padStart(2, '0')

actual fun clockTime(wallMs: Long): String {
    val d = Date(wallMs.toDouble())
    return "${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}.${d.getMilliseconds().toString().padStart(3, '0')}"
}

actual fun reportTimestamp(): String {
    val d = Date()
    val offset = -d.getTimezoneOffset() // minutes east of UTC
    val sign = if (offset < 0) "-" else "+"
    val zone = sign + two(abs(offset) / 60) + two(abs(offset) % 60)
    return "${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} " +
        "${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())} $zone"
}
